import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Filter } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const getMonthRange = () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(new Date(n.getFullYear(), n.getMonth()+1, 0).getDate()).padStart(2,'0')}` }; };

const ProfitSummary = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, revenue_items: [], expense_items: [] });
  const mr = getMonthRange();
  const [startDate, setStartDate] = useState(mr.start);
  const [endDate, setEndDate] = useState(mr.end);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/reports/profit?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${BACKEND_URL}/api/reports/profit/export-pdf?${params}`, '_blank');
  };

  const exportCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    window.open(`${BACKEND_URL}/api/reports/profit/export-csv?${params}`, '_blank');
  };

  const s = data.summary || {};
  const netProfit = s.net_profit || 0;
  const grossProfit = s.gross_profit || 0;
  const profitColor = netProfit >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]';
  const profitBg = netProfit >= 0 ? 'bg-[#DBEAFE] border-[#BFDBFE]' : 'bg-[#FEF2F2] border-[#FCA5A5]';

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="profit-summary-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="profit-summary-title">Profit Summary</h1>
            <div className="flex gap-2">
              <button onClick={exportPdf} className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-sm text-sm" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Export PDF</button>
              <button onClick={exportCsv} className="flex items-center gap-2 border border-[#e5e7eb] bg-white text-[#1a1a2e] px-4 py-2 rounded-sm text-sm hover:bg-[#F5F5F5] transition-colors" data-testid="export-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="profit-report-filters">
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="start-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6b7280] mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm" data-testid="end-date-input" />
            </div>
            <div className="flex items-end">
              <button onClick={fetchReport} className="w-full flex items-center justify-center gap-2 bg-[#1a1a2e] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#27272A] transition-colors" data-testid="apply-filters-btn"><Filter className="w-4 h-4" />Apply</button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-revenue-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Total Revenue</p>
              <p className="text-xl font-semibold text-[#1E40AF]">{fmtRs(s.total_revenue)}</p>
            </div>
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-5" data-testid="cogs-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Cost of Goods Sold</p>
              <p className="text-xl font-semibold text-[#92400E]">{fmtRs(s.cogs)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="gross-profit-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${grossProfit >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]'}`}>{fmtRs(s.gross_profit)}</p>
              {s.gross_margin != null && <p className="text-xs text-[#6b7280] mt-1">{s.gross_margin}% margin</p>}
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="total-expenses-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Operating Expenses</p>
              <p className="text-xl font-semibold text-[#EF4444]">{fmtRs(s.total_expenses)}</p>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="net-profit-card">
              <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Net Profit</p>
              <p className={`text-xl font-semibold ${profitColor}`}>{fmtRs(s.net_profit)}</p>
              {s.net_margin != null && <p className="text-xs text-[#6b7280] mt-1">{s.net_margin}% margin</p>}
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#6b7280]">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Revenue Section */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[#e5e7eb] bg-[#EFF6FF]">
                  <h3 className="text-sm font-semibold text-[#1E40AF]">Revenue</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]" data-testid="revenue-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4 text-left">Account</th>
                      <th className="py-3 px-4 text-right">Amount (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.revenue_items || []).map((item, i) => (
                      <tr key={i} className="border-b border-[#e5e7eb]">
                        <td className="py-3 px-4 text-[#1a1a2e]">{item.account_name}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(item.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#EFF6FF] font-bold">
                      <td className="py-3 px-4 text-[#1E40AF]">Total Revenue</td>
                      <td className="py-3 px-4 text-right text-[#1E40AF]">{fmtRs(s.total_revenue)}</td>
                    </tr>
                  </tbody>
                  </table>
                </div>
              </div>

              {/* Expense Section */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[#e5e7eb] bg-[#FEF2F2]">
                  <h3 className="text-sm font-semibold text-[#991B1B]">Expenses</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]" data-testid="expense-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4 text-left">Account</th>
                      <th className="py-3 px-4 text-right">Amount (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.expense_items || []).map((item, i) => (
                      <tr key={i} className="border-b border-[#e5e7eb]">
                        <td className="py-3 px-4 text-[#1a1a2e]">{item.account_name}</td>
                        <td className="py-3 px-4 text-right text-[#1a1a2e]">{fmtRs(item.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#FEF2F2] font-bold">
                      <td className="py-3 px-4 text-[#991B1B]">Total Expenses</td>
                      <td className="py-3 px-4 text-right text-[#991B1B]">{fmtRs(s.total_expenses)}</td>
                    </tr>
                  </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Net Profit Bottom Line */}
          {!loading && (
            <div className={`border rounded-sm p-4 sm:p-6 text-center ${profitBg}`} data-testid="net-profit-bottom">
              <p className="text-sm uppercase tracking-wider text-[#6b7280] mb-1">
                Net Profit = Revenue ({fmtRs(s.total_revenue)}) − COGS ({fmtRs(s.cogs)}) − Expenses ({fmtRs(s.total_expenses)})
              </p>
              <p className={`text-3xl sm:text-4xl font-bold ${profitColor}`} data-testid="net-profit-value">{fmtRs(netProfit)}</p>
              {s.net_margin != null && (
                <p className="text-sm text-[#6b7280] mt-2">Net Profit Margin: {s.net_margin}%</p>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfitSummary;
